import taxCreditUrl from '~/constants/common/taxCreditUrl';
import type { QuestionAndAnswer } from '~/contracts/ev-tax-credit';
import ExternalSiteLink from '../ExternalSiteLink';

function FaqExternalLink(props: { url: string; children: React.ReactNode }) {
  return <ExternalSiteLink section="FAQ" {...props} />;
}

const questionAndAnswers: QuestionAndAnswer[] = [
  {
    question: 'How does the government calculate the EV Tax Credit?',
    answer: (
      <p>
        The used EV tax credit for any eligible vehicle will equal 30% of the
        vehicle’s sale price (including any non-government fees, such as
        shipping and delivery fees), up to a maximum credit of $4,000.
      </p>
    )
  },
  {
    question: 'What are some of the financial benefits of purchasing an EV?',
    answer: (
      <p>
        Beyond the initial tax savings, used EVs can offer ongoing cost
        benefits. For example, according to the Department of Energy, electric
        vehicles{' '}
        <FaqExternalLink url="https://afdc.energy.gov/vehicles/electric_maintenance.html">
          generally require less maintenance
        </FaqExternalLink>{' '}
        than their gasoline counterparts because they have fewer moving parts.
        EVs also run on electricity, which is{' '}
        <FaqExternalLink url="https://yaleclimateconnections.org/2022/06/driving-with-electricity-is-much-cheaper-than-with-gasoline/">
          typically less expensive per mile
        </FaqExternalLink>{' '}
        than gasoline. State-level incentives, such as carpool lane access and
        reduced registration fees, can also sweeten the deal.
      </p>
    )
  },
  {
    question: 'What are the environmental benefits of buying an EV?',
    answer: (
      <p>
        Even when accounting for electricity used for charging,{' '}
        <FaqExternalLink url="https://www.epa.gov/greenvehicles/electric-vehicle-myths">
          EVs usually have a smaller carbon footprint
        </FaqExternalLink>{' '}
        than gasoline cars. EVs are also quieter than traditional combustion
        vehicles –{' '}
        <FaqExternalLink url="https://www.fastcompany.com/90774779/heres-what-science-says-about-electric-cars-and-their-impact-on-noise-pollution">
          creating less noise pollution.
        </FaqExternalLink>
      </p>
    )
  },
  {
    question:
      'How does the federal EV tax credit for used EVs compare to the tax credit for new EVs?',
    answer: (
      <p>
        The new EV tax credit can reach up to $7,500, while the used EV tax
        credit can reach up to $4,000. Additionally, new and used vehicles have
        different eligibility requirements to qualify for an EV tax credit.
      </p>
    )
  },
  {
    question:
      'Is the used EV tax credit the same for used vehicles purchased in 2023 vs. 2024?',
    answer: (
      <>
        <p>
          The maximum amount you can receive from a used EV tax credit ($4,000)
          remains the same, but car buyers may be able to receive the credit
          more easily. Starting January 1, 2024, customers may be able to
          transfer the credit to the dealer to have the total purchase price of
          the vehicle reduced.
        </p>
        <p>
          If you purchase from a dealer that is not Carvana, you should ask
          whether the dealer is participating in the used EV tax credit program,
          and will allow you to transfer your tax credit to the dealership.
        </p>
      </>
    )
  },
  {
    question: 'How can I benefit from the federal used EV tax credit?',
    answer: (
      <>
        <p>
          There are two ways that the federal used EV tax credit can be applied.
          For qualified purchases made before the end of 2023, a taxpayer
          claiming the credit can lower the amount of federal income tax they
          owe by up to $4,000.
        </p>
        <p>
          For qualified purchases made after January 1, 2024, qualified
          purchasers of a qualified EV can either claim the tax credit
          themselves or receive up to $4,000 off the cost of the total purchase
          price of the vehicle.
        </p>
        <p>
          If you apply the tax credit to reduce the amount of federal taxes you
          owe, you cannot get back more on the credit than you owe in taxes (in
          other words, it will not be added to any tax refund you get from the
          IRS), and any excess credit cannot be applied to future years.
        </p>
      </>
    )
  },
  {
    question:
      'Will I need to file a specific tax form to claim the 2024 EV tax credit?',
    answer: (
      <>
        <p>
          Yes. You’ll need to file form 8936 or another form designated by the
          IRS with your income tax return whether you transfer the credit to the
          dealer or claim the credit on your own return.
        </p>
        <p>
          If the seller or dealer is not registered with the IRS, you won’t
          receive the benefits of the used EV tax credit.
        </p>
      </>
    )
  },
  {
    question: 'What does the $25,000 vehicle price limit include?',
    answer: (
      <>
        <p>
          The following items are not included in the $25,000 vehicle price
          limit:
        </p>
        <ul className="ml-2xl list-disc">
          <li>Tax, title, and registration costs; and</li>
          <li>
            Non-car products like vehicle service contracts, GAP waivers, or
            insurance.
          </li>
        </ul>
        <p>
          However, shipping or delivery charges and discounts applied at
          purchase are included in the $25,000 vehicle price limit.
        </p>
      </>
    )
  },
  {
    question:
      'Is there a limit on the number of vehicles I can claim an EV tax credit for?',
    answer: (
      <p>
        No, but you may only claim a used EV tax credit{' '}
        <FaqExternalLink url={taxCreditUrl}>
          once every three years
        </FaqExternalLink>
        .
      </p>
    )
  },
  // TODO: Waiting on copy
  // {
  //   question: 'How does applying for the EV Tax Credit Work at Carvana?',
  //   answer: (
  //     <>
  //       <p>
  //         For example, let’s say you decide to purchase a used EV with a price
  //         of $23,000, with a $1,000 down payment [COPY TBD, FILL OUT WITH USE
  //         CASE EXAMPLE].
  //       </p>
  //       <p>
  //         I think we need something about how buyers will transact with Carvana
  //         and have the discount applied. Not sure how to word this, but
  //         something about the “during purchase checkout” kind of instructions or
  //         information
  //       </p>
  //     </>
  //   )
  // },
  {
    question: 'What if I have other questions about the used EV tax credit?',
    answer: (
      <p>
        To answer other questions about the used EV tax credit, we recommend{' '}
        <FaqExternalLink url="https://www.irs.gov/newsroom/topic-f-frequently-asked-questions-about-claiming-the-previously-owned-clean-vehicles-credit">
          visiting the IRS Frequently-Asked Questions page
        </FaqExternalLink>
        .
      </p>
    )
  }
];

export default questionAndAnswers;
